import { Cascader, Flex, SearchFilterTag, Tag, theme } from '@/components';
import {
  CommentPublishTypeLabelModifier,
  CommentReadStatusLabelModifier,
} from '@/features/comment/models/comment';
import {
  CommentResolvedStatusLabelModifier,
  type CommentSearchFormSchema,
} from '@/features/comment/models/search';
import { pinpointOptionsBySheet } from '@/features/comment/utils/pinpoint-options-by-sheet';
import { useTagOptions } from '@/features/tag/hooks';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useUser } from '@/features/tenant-manager/user/_hooks/user';
import { parseUserOptionLabel } from '@/features/tenant-manager/user/_utils/user';
import { getCreatedMonthValueText } from '@/utils';
import type { DefaultOptionType } from 'antd/es/cascader';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

type FormValues = z.infer<typeof CommentSearchFormSchema>;

type FilterTag<T> = {
  label: string;
} & ({ closable: false } | { closable: true; clearValue: T });

type FilterTags = {
  [K in keyof FormValues]?: FilterTag<FormValues[K]>;
};

export const CommentListSearchFilterTags: FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext<FormValues>();

  const organizationsOptions = useOrganizationOptionsRecursive();

  const [
    createdMonthRange,
    keyword,
    publishType,
    resolvedStatus,
    labelId,
    tagIds,
    authorId,
    assigneeId,
    fiscalYear,
    organizationIds,
    readStatus,
    pinpointKeys,
  ] = watch([
    'createdMonthRange',
    'keyword',
    'publishType',
    'resolvedStatus',
    'labelId',
    'tagIds',
    'authorId',
    'assigneeId',
    'fiscalYear',
    'organizationIds',
    'readStatus',
    'pinpointKeys',
  ]);

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });
  const tagOptions = useTagOptions({ type: 'comment' });
  const author = useUser(authorId ?? '');
  const assignee = useUser(assigneeId ?? '');
  const filterTags = {
    fiscalYear: fiscalYear
      ? {
          label: t('prefix-fiscal-year-with-value', {
            value: fiscalYear?.year(),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    createdMonthRange: createdMonthRange?.some((v) => !!v)
      ? {
          label: t('prefix-created-month-with-value', {
            value: getCreatedMonthValueText(createdMonthRange),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    keyword: keyword
      ? {
          label: t('prefix-keyword-with-value', { value: keyword }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    resolvedStatus: {
      label: t('comment.prefix.resolved-status-with-value', {
        value: CommentResolvedStatusLabelModifier[resolvedStatus],
      }),
      closable: false,
    },
    publishType: {
      label: t('comment.prefix.publish-type-with-value', {
        value: CommentPublishTypeLabelModifier[publishType],
      }),
      closable: false,
    },
    labelId: labelId
      ? {
          label: t('comment.prefix.label-with-value', {
            value:
              hierarchyLabelOptions?.find((label) => label.value === labelId)
                ?.label ?? t('not-exist-in-option'),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    tagIds: tagIds?.length
      ? {
          label: t('prefix-tag-with-value', {
            value:
              tagIds
                .map(
                  (tagId) =>
                    tagOptions.data?.find(
                      (commentTagLabel) => commentTagLabel.value === tagId,
                    )?.label ?? tagId,
                )
                .join(',') ?? '',
          }),
          clearValue: null,
          closable: true,
        }
      : undefined,
    authorId: author.data
      ? {
          label: t('comment.prefix.author-with-value', {
            value: parseUserOptionLabel(author.data),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    assigneeId: assignee.data
      ? {
          label: t('comment.prefix.assignee-with-value', {
            value: parseUserOptionLabel(assignee.data),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    readStatus: readStatus
      ? {
          label: CommentReadStatusLabelModifier[readStatus],
          closable: false,
        }
      : undefined,
  } as const satisfies FilterTags;

  return (
    <Flex vertical gap={4}>
      <div>
        <Flex
          gap={4}
          style={{
            background: token.colorBgLayout,
            padding: 5,
            flexWrap: 'wrap',
            display: 'inline-flex',
          }}
        >
          {Object.entries(filterTags).map(
            ([key, value]) =>
              value && (
                <SearchFilterTag
                  key={key}
                  label={value.label}
                  closable={value.closable}
                  onClose={() => {
                    invariant(value.closable, t('unreachable-code'));
                    setValue(key as keyof FormValues, value.clearValue);
                  }}
                />
              ),
          )}
        </Flex>
      </div>
      <Flex gap={8}>
        {organizationIds?.length > 0 && (
          <Cascader
            value={organizationIds}
            options={organizationsOptions.data}
            loading={organizationsOptions.isPending}
            multiple
            allowClear={false}
            open={false}
            maxTagCount="responsive"
            style={{ width: 240, overflow: 'hidden' }}
            tagRender={({ label }) => (
              <Flex align="center" justify="center">
                <Tag
                  style={{
                    width: 200,
                    color: token.colorTextBase,
                    fontSize: token.fontSize,
                    margin: 2,
                    padding: 8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  color={token.colorPrimaryBg}
                >
                  {label}
                </Tag>
              </Flex>
            )}
            displayRender={(labels, selectedOptions) => (
              <DisplayRender
                selectedOptions={selectedOptions}
                labels={labels}
              />
            )}
          />
        )}
        {pinpointKeys?.length > 0 && (
          <Cascader
            value={pinpointKeys.map((key) => [key])}
            options={pinpointOptionsBySheet}
            multiple
            allowClear={false}
            open={false}
            maxTagCount="responsive"
            style={{ width: 240, overflow: 'hidden' }}
            tagRender={({ label }) => (
              <Flex align="center" justify="center">
                <Tag
                  style={{
                    width: 200,
                    color: token.colorTextBase,
                    fontSize: token.fontSize,
                    margin: 2,
                    padding: 8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  color={token.colorPrimaryBg}
                >
                  {label}
                </Tag>
              </Flex>
            )}
          />
        )}
      </Flex>
    </Flex>
  );
};

const DisplayRender = ({
  labels,
  selectedOptions,
}: { labels: string[]; selectedOptions?: DefaultOptionType[] }) => {
  const { t } = useTranslation();
  if (selectedOptions?.some((v) => v === null)) {
    return t('not-exist-in-option');
  }
  return labels.slice(-1);
};
